import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, Loader2, Paperclip, X, FileText, Eye, AlertCircle } from "lucide-react";
import { API_BASE_URL } from "@/lib/api";
import RecipientChipsInput, { isValidEmailAddr } from "@/components/RecipientChipsInput";

const API = API_BASE_URL;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const ALLOWED_EXTS = [".jpg", ".jpeg", ".png", ".pdf"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function formatSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ComposeEmail({
  open, onOpenChange, aliasId, aliasAddress, aliases,
  onSent, sendsRemaining, sendsTotal, isImpersonating, prefill,
}) {
  // `to` is now an array of recipient addresses (chips).
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedAliasId, setSelectedAliasId] = useState(aliasId);
  const [sendError, setSendError] = useState("");
  const fileInputRef = useRef(null);

  // Reset selected alias when aliasId prop changes
  useEffect(() => {
    if (aliasId) setSelectedAliasId(aliasId);
  }, [aliasId]);

  // Apply prefill for reply/forward — `to` may be a string or string[]
  useEffect(() => {
    if (open && prefill) {
      if (prefill.to !== undefined) {
        const arr = Array.isArray(prefill.to)
          ? prefill.to
          : (prefill.to || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        setTo(arr);
      }
      if (prefill.subject !== undefined) setSubject(prefill.subject);
      if (prefill.body !== undefined) setText(prefill.body);
      if (prefill.aliasId) setSelectedAliasId(prefill.aliasId);
      setSendError("");
    }
  }, [open, prefill]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSendError("");
    }
  }, [open]);

  const activeAliases = (aliases || []).filter(a => a.is_active);
  const currentAlias = activeAliases.find(a => a.alias_id === selectedAliasId)
    || activeAliases.find(a => a.alias_id === aliasId)
    || activeAliases[0];
  const currentAliasAddress = currentAlias?.alias_address || aliasAddress;
  const currentAliasId = currentAlias?.alias_id || aliasId;

  // Validation — at least one recipient + all valid
  const validRecipients = to.filter((r) => isValidEmailAddr(r));
  const hasInvalid = to.some((r) => !isValidEmailAddr(r));
  const missingTo = to.length === 0;
  const missingSubject = !subject.trim();
  const limitReached = sendsRemaining != null && sendsRemaining <= 0;

  let disableReason = "";
  if (limitReached) disableReason = "Send limit reached";
  else if (missingTo) disableReason = "At least one recipient is required";
  else if (hasInvalid) disableReason = "One or more recipients are invalid";
  else if (missingSubject) disableReason = "Subject is required";

  const canSend = !sending && !disableReason;

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = [];
    for (const f of selected) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_TYPES.includes(f.type)) {
        toast.error(`File type not allowed: ${f.name}. Only JPG, PNG, PDF.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`File too large: ${f.name} (max 5 MB)`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendError("");
    const recipientsCsv = validRecipients.join(",");
    try {
      let response;
      if (files.length > 0) {
        const formData = new FormData();
        formData.append("alias_id", currentAliasId);
        formData.append("to_email", recipientsCsv);
        formData.append("subject", subject.trim());
        formData.append("text", text.trim());
        files.forEach((f) => formData.append("files", f));

        const fetchHeaders = {};
        const impToken = sessionStorage.getItem("impersonate_token");
        if (impToken) {
          fetchHeaders["Authorization"] = `Bearer ${impToken}`;
        }

        const res = await fetch(`${API}/api/nina/send`, {
          method: "POST",
          body: formData,
          credentials: "include",
          headers: fetchHeaders,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw { response: { data: err } };
        }
        response = { data: await res.json() };
      } else {
        const { default: api } = await import("@/lib/api");
        response = await api.post("/nina/send", {
          alias_id: currentAliasId,
          to_email: recipientsCsv,
          subject: subject.trim(),
          text: text.trim(),
        });
      }

      toast.success(validRecipients.length > 1 ? `Email sent to ${validRecipients.length} recipients` : "Email sent");
      setTo([]); setSubject(""); setText(""); setFiles([]);
      onOpenChange(false);
      if (onSent) onSent(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "send_limit_reached") {
        toast.error("You've reached your free send limit.");
        onOpenChange(false);
        if (onSent) onSent({ limit_reached: true });
      } else {
        const msg = detail || "Send failed. Please try again.";
        setSendError(msg);
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const totalAttachSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setFiles([]); }}>
      {/* CORE_001 reference implementation.
            • `max-h-modal-vvh` caps the dialog at the visual viewport
              minus 24 px so it always fits the area NOT covered by the
              soft keyboard (visualViewport-driven, set in index.css from
              the useViewport hook).
            • `flex flex-col` lets the body region scroll while the
              header / footer stay pinned.
            • `pb-kb` (padding-bottom keyboard inset) keeps the action
              row above the keyboard automatically — no device-specific
              code, just the measured `--kb-inset` CSS variable.
            • `pb-safe` ensures the action row also clears the iOS home
              indicator when the keyboard isn't open.
          The `style={{ paddingBottom: ... }}` legacy override is gone
          on purpose; .pb-kb + .pb-safe are stacked via a utility-only
          approach so future migrations don't need to touch JS. */}
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-[520px] max-h-modal-vvh p-0 flex flex-col overflow-hidden"
        data-testid="compose-dialog"
      >
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">
              {prefill?.mode === "reply" ? "Reply" : prefill?.mode === "forward" ? "Forward" : "Compose email"}
            </DialogTitle>
            {isImpersonating && (
              <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-600" data-testid="compose-impersonation-indicator">
                <Eye size={11} className="shrink-0" />
                <span>Sending as this user — action will be logged</span>
              </div>
            )}
            {sendsRemaining != null && (
              <p className="text-[11px] text-zinc-500 font-mono mt-1" data-testid="compose-send-counter">
                {sendsTotal - sendsRemaining} of {sendsTotal} emails sent
              </p>
            )}
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3" data-testid="compose-scroll-region">

        <div className="space-y-3 py-2">
          {/* From — alias selector */}
          <div>
            <Label className="text-xs text-[#888888] font-medium">From</Label>
            {activeAliases.length > 1 ? (
              <Select value={selectedAliasId} onValueChange={setSelectedAliasId}>
                <SelectTrigger className="mt-1 h-9" data-testid="compose-from-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activeAliases.map((a) => (
                    <SelectItem key={a.alias_id} value={a.alias_id} data-testid={`compose-alias-option-${a.alias_id}`}>
                      <span className="text-sm">{a.label || a.alias_address.split("@")[0]}</span>
                      <span className="text-xs text-zinc-500 ml-2">{a.alias_address}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="mt-1 text-sm text-[#555555] bg-[#F5F5F5] px-3 py-2 rounded-md border border-[#E5E5E5]" data-testid="compose-from-display">
                {currentAlias?.label ? `${currentAlias.label} — ` : ""}{currentAliasAddress}
              </p>
            )}
          </div>

          {/* To — multi-recipient chips */}
          <div>
            <Label htmlFor="compose-to" className="text-xs text-[#888888] font-medium">To</Label>
            <div className="mt-1">
              <RecipientChipsInput
                value={to}
                onChange={setTo}
                placeholder="recipient@example.com (comma-separated)"
                testId="compose-to"
                autoFocus={!prefill?.to}
              />
            </div>
            {to.length > 1 && (
              <p className="text-[11px] text-zinc-500 mt-1" data-testid="compose-to-count">
                {to.length} recipients
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="compose-subject" className="text-xs text-[#888888] font-medium">Subject</Label>
            <Input
              id="compose-subject"
              placeholder="Enter subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1"
              data-testid="compose-subject-input"
            />
          </div>

          {/* Body */}
          <div>
            <Label htmlFor="compose-body" className="text-xs text-[#888888] font-medium">Message</Label>
            <Textarea
              id="compose-body"
              placeholder="Write your message..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="mt-1 min-h-[120px] resize-none"
              data-testid="compose-body-input"
            />
          </div>

          {/* Forward attachment notice */}
          {prefill?.mode === "forward" && prefill?.hasAttachments && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F5F5F5] border border-[#E5E5E5] rounded text-[11px] text-zinc-500" data-testid="compose-forward-attachment-notice">
              <Paperclip size={12} className="shrink-0" />
              <span>Original attachments are not forwarded. You can add new files below.</span>
            </div>
          )}

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                type="button" variant="outline" size="sm" className="h-8 text-xs text-[#888888]"
                onClick={() => fileInputRef.current?.click()}
                data-testid="compose-attach-button"
              >
                <Paperclip size={14} className="mr-1.5" /> Attach files
              </Button>
              <span className="text-[10px] text-zinc-500">JPG, PNG, PDF — max 5 MB each</span>
            </div>
            <input
              ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" multiple
              className="hidden" onChange={handleFileSelect} data-testid="compose-file-input"
            />
            {files.length > 0 && (
              <div className="space-y-1" data-testid="compose-attached-files">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 bg-[#F5F5F5] rounded-md px-2.5 py-1.5 border border-[#E5E5E5]" data-testid={`compose-file-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-zinc-500 shrink-0" />
                      <span className="text-xs text-[#555555] truncate">{f.name}</span>
                      <span className="text-[10px] text-zinc-500 font-mono shrink-0">{formatSize(f.size)}</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-zinc-400 hover:text-red-500 shrink-0" data-testid={`compose-file-remove-${i}`}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-zinc-500 font-mono">{files.length} file{files.length > 1 ? "s" : ""} — {formatSize(totalAttachSize)} total</p>
              </div>
            )}
          </div>
        </div>
        </div>
        {/* End scroll-region (CORE_001). Header + footer below stay
            pinned even on tiny phones with the keyboard up. */}

        {/* Send error — stays inside the visible footer region so the
            user always sees why their send failed. */}
        {sendError && (
          <div className="mx-6 mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-600" data-testid="compose-send-error">
            <AlertCircle size={13} className="shrink-0" />
            <span>{sendError}</span>
          </div>
        )}

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-col px-6 pt-3 pb-kb border-t border-[#E5E5E5]">
          {limitReached && (
            <div className="text-center space-y-1.5 py-1" data-testid="compose-limit-reached">
              <p className="text-sm text-[#555555] font-medium">You've reached your free send limit.</p>
              <button onClick={() => { onOpenChange(false); window.location.href = "/pricing"; }} className="text-xs font-semibold text-blue-600 hover:text-blue-600" data-testid="compose-upgrade-cta">
                Upgrade to send more emails
              </button>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            {/* Disable reason */}
            {disableReason && !limitReached && (
              <p className="text-[11px] text-zinc-400 flex-1 truncate">{disableReason}</p>
            )}
            {!disableReason && <div className="flex-1" />}
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); setFiles([]); }} data-testid="compose-cancel-button">
                Cancel
              </Button>
              <Button
                size="sm" onClick={handleSend} disabled={!canSend}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="compose-send-button"
                title={disableReason || undefined}
              >
                {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                {files.length > 0 ? `Send with ${files.length} file${files.length > 1 ? "s" : ""}` : "Send"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
